import clsx from "clsx";
import type { ComponentProps } from "react";

import { COLORS_VALUES } from "@shared/constants";
import { useImageMaskRef } from "@shared/hooks/use-image-mask-ref";
import { BrandIcon } from "@shared/ui/brand-icon";
import { Button } from "@shared/ui/button";
import { Image } from "@shared/ui/image";
import { Body, Heading } from "@shared/ui/typography";
import { formatNumberWithSpaces } from "@shared/utils/string";

import type { MerchType } from "@/_pages/merch/model";

import s from "./shop-card.module.scss";

export type ShopCardProps = ComponentProps<"div"> & {
  data: MerchType;
};

export const ShopCard = (props: ShopCardProps) => {
  const { data } = props;

  const style: React.CSSProperties & { ["--hover-color"]: string } = {
    "--hover-color":
      COLORS_VALUES[data?.icon?.fillColor as keyof typeof COLORS_VALUES],
  };

  const $imageRef = useImageMaskRef();

  return (
    <article className={clsx(s.root)} style={style}>
      <Button href={`/shop/${data.uid}`} className={s.wrapper}>
        <div className={s.icons}>
          {data?.icon && (
            <BrandIcon
              name={data?.icon?.icon?.name}
              fillColor={data?.icon?.fillColor}
              strokeColor={data?.icon?.strokeColor}
              className={s.typeIcon}
            />
          )}
          <BrandIcon name="arrow" fillColor="white" className={s.arrowIcon} />
        </div>
        <div className={s.top} />
        <div className={s.middle} ref={$imageRef}>
          <Image
            src={data.mainImage?.url!}
            alt={data.mainImage?.alternativeText || "shop item image"}
            height="100%"
            className={s.image}
          />
        </div>

        {data?.title && (
          <div className={s.bottom}>
            <Heading level={6} tag="h2" className={s.name}>
              {data?.title}
            </Heading>
            <Body className={s.price}>
              {data?.price ? formatNumberWithSpaces(data?.price) : ""}
            </Body>
          </div>
        )}
      </Button>
    </article>
  );
};

ShopCard.displayName = "ShopCard";
